import React, { useCallback, useEffect } from 'react';
import { Box, List, RawButton, Text } from '@common-fe/common-fe';
import styled from 'styled-components';

import { ROUTES } from '@/common';
import Permissions from '@/common/permissions';
import ManyItemsTooltip from '@/components/elements/ManyItemsTooltip/ManyItemsTooltip';
import { useHasAccess, useHistory } from '@/modules/core/hooks';
import theme from '@/styles/theme';

import { IFieldLevelAccessSetup } from '../role.types';
import { useHighlightNewFieldLevelAccessStore } from '../stores';

interface Props extends IFieldLevelAccessSetup {
  onOpenSummary: (id: string) => void;
  onSetCopyModal: (id: string) => void;
}

const StyledText = styled(Text)`
  &:hover {
    color: ${({ theme }) => theme.colors.textAccent};
  }
`;

const TIMEOUT = 3500;

const FieldLevelAccessListItem: React.FC<Props> = ({
  id,
  name,
  description,
  roles,
  onSetCopyModal,
  onOpenSummary,
}) => {
  const isAvailableToEdit = useHasAccess([
    {
      permission: Permissions.EDIT_ROLES_AND_SECURITY,
    },
  ]);
  const history = useHistory();
  const highlightedId = useHighlightNewFieldLevelAccessStore((state) => state.highlightId);
  const setHighlightedId = useHighlightNewFieldLevelAccessStore((state) => state.setHighlightId);

  useEffect(() => {
    if (highlightedId === id) {
      setTimeout(() => {
        setHighlightedId('');
      }, TIMEOUT);
    }
  }, [highlightedId, id, setHighlightedId]);
  const handleOpenSummary = useCallback(() => {
    onOpenSummary(id);
  }, [id, onOpenSummary]);
  const handleOpenCopyRole = useCallback(() => {
    onSetCopyModal(id);
  }, [id, onSetCopyModal]);

  const handleOpenEdit = useCallback(() => {
    history.push(ROUTES.FIELD_LEVEL_ACCESS_SETUP_EDIT(id));
  }, [history, id]);
  return (
    <List.Item
      onClick={handleOpenSummary}
      testId="field-level-access-list_item"
      isHighlighted={highlightedId === id}
    >
      <Box direction="column" style={{ flex: 2 }}>
        <RawButton onClick={handleOpenSummary} style={{maxWidth: '250px', display: 'block'}}>
          <Box>
            <StyledText truncate title={name}>
              {name}
            </StyledText>
        
            <List.Item.Text size="small" color="textSecondary" truncate title={description}>
              {description}
            </List.Item.Text>
          </Box>
        </RawButton>
      </Box>

      <Box align="center" direction="row" style={{ flex: 4 }}>
        <Box width={{ min: '400px' }}>
          <ManyItemsTooltip
            items={roles || []}
            itemsTextStyles={{
              fontSize: '14px',
              lineHeight: '20px',
              color: theme.colors.textBody,
              fontWeight: 400,
            }}
          />
        </Box>
      </Box>

      <List.Item.Options testId="role_options_button">
        <List.Item.Options.Item testId="role_options_details_button" onClick={handleOpenSummary}>
          View summary
        </List.Item.Options.Item>
        {isAvailableToEdit && (
          <List.Item.Options.Item testId="role_options_edit_button" onClick={handleOpenEdit}>
            Edit
          </List.Item.Options.Item>
        )}

        {isAvailableToEdit && (
          <List.Item.Options.Item onClick={handleOpenCopyRole}>
            Copy properties
          </List.Item.Options.Item>

        )}
      </List.Item.Options>
    </List.Item>
  );
};

export default FieldLevelAccessListItem;
